import { useEffect, useState } from 'react';
import { auth, db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { VenueAdsConfig } from '../types';

const defaultConfig: VenueAdsConfig = {
  enabled: false,
  placement: 'full-screen',
  defaultDurationSeconds: 25,
  minIntervalMinutes: 5,
  triggers: { whenNoQueue: true, afterSongsCount: 0, duringSplash: true },
  sources: [],
};

export const useVenueAdsConfig = (hostUserId?: string | null, partyId?: string | null) => {
  const [config, setConfig] = useState<VenueAdsConfig>(defaultConfig);
  const [loading, setLoading] = useState<boolean>(!!hostUserId);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!hostUserId) { setConfig(defaultConfig); setLoading(false); return; }
      if (!auth.currentUser) { setConfig(defaultConfig); setLoading(false); return; }
      try {
        setLoading(true);
        // Load user-level config
        const userSnap = await getDoc(doc(db, 'users', hostUserId));
        const userCfg = userSnap.exists() ? ((userSnap.data() as any).venueAdsConfig || {}) as Partial<VenueAdsConfig> : {};

        // Load optional party-level override under control/venueAdsConfig
        let partyCfg: Partial<VenueAdsConfig> = {};
        if (partyId) {
          const partyOverrideSnap = await getDoc(doc(db, 'parties', partyId, 'control', 'venueAdsConfig'));
          if (partyOverrideSnap.exists()) {
            partyCfg = (partyOverrideSnap.data() as any) as Partial<VenueAdsConfig>;
          }
        }

        const base = { ...defaultConfig, ...userCfg } as any;
        const mergedTriggers = { ...defaultConfig.triggers, ...(userCfg.triggers || {}), ...(partyCfg.triggers || {}) };
        const mergedSourcesArr = (partyCfg.sources ?? userCfg.sources ?? []) as any[];
        const normalizedSources = Array.isArray(mergedSourcesArr) ? mergedSourcesArr.map((s: any, idx: number) => ({
          id: s.id || String(idx + 1),
          type: (s.type === 'video' ? 'video' : 'image'),
          url: s.url || '',
          enabled: s.enabled !== false,
          label: s.label || '',
          durationSeconds: s.durationSeconds,
        })) : [];
        const merged: VenueAdsConfig = {
          ...base,
          ...userCfg,
          ...partyCfg,
          triggers: mergedTriggers,
          sources: normalizedSources,
        } as VenueAdsConfig;
        if (!cancelled) setConfig(merged);
      } catch {
        if (!cancelled) setConfig(defaultConfig);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [hostUserId, partyId]);

  return { config, loading };
};
